import { Input } from "../../input";
import { Swiper, SwiperRef, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { Address, CompanyDetails } from "../../types/Types";
import { InvoiceOptionCard } from "./invoice-option-card/InvoiceOptionCard";
import { useEffect, useMemo, useRef } from "react";
import { isCompanyDetails } from "../../utils/IsCompanyDetails";

type InvoiceOptionsSliderProps = {
  textsMapping: Record<string, string>;
  options: (Address | CompanyDetails)[];
  selectedOptionIndex: number;
  onOptionSelect: (index: number) => void;
  onOpenDrawer: (mode: "manage" | "add" | false) => void;
  setInvoiceEmail: (email: string) => void;
};

export function InvoiceOptionsSlider({
  textsMapping,
  options,
  selectedOptionIndex,
  onOptionSelect,
  onOpenDrawer,
}: InvoiceOptionsSliderProps) {
  const selectedOption = useMemo(
    () => options[selectedOptionIndex],
    [options, selectedOptionIndex]
  );

  const ref = useRef<SwiperRef>(null);

  useEffect(() => {
    if (selectedOptionIndex !== null) {
      swipeToSlide(selectedOptionIndex);
    }
  }, [selectedOptionIndex]);

  function swipeToSlide(index: number) {
    ref.current?.swiper.slideTo(index);
  }

  return (
    <>
      <section className="checkout-section invoice-option-swiper">
        <h3 className="checkout-section__title">
          {textsMapping.billingDataAddress}
        </h3>

        <p>{textsMapping.billingDataText}</p>

        <Swiper
          ref={ref}
          slidesPerView={1}
          spaceBetween={8}
          modules={[Navigation]}
          navigation
          breakpoints={{ 1025: { slidesPerView: 2 } }}
        >
          {options.map((option, index) => (
            <SwiperSlide key={index} onFocus={() => swipeToSlide(index)}>
              <InvoiceOptionCard
                isActive={selectedOptionIndex === index}
                option={option}
                onClick={() => onOptionSelect(index)}
              />
            </SwiperSlide>
          ))}

          <SwiperSlide onFocus={() => swipeToSlide(options.length)}>
            <button
              className="invoice-option-card button--link add-company-slide"
              onClick={() => onOpenDrawer("add")}
            >
              {textsMapping.addInvoiceOptionText}
            </button>
          </SwiperSlide>
        </Swiper>

        <button
          className="button button--link icon-pencil"
          onClick={() => onOpenDrawer("manage")}
        >
          {textsMapping.addOrEditInvoiceOptionText}
        </button>
      </section>

      {selectedOption !== undefined && isCompanyDetails(selectedOption) && (
        <>
          <section
            key={selectedOptionIndex}
            className="checkout-section order-reference"
          >
            <h3 className="checkout-section__title">
              {textsMapping.invoiceCompanyOrderReferenceTitle}
            </h3>

            <p>{textsMapping.invoiceCompanyOrderReferenceText}</p>

            <Input
              resourcedTexts={textsMapping}
              inputType="text"
              inputLabel={textsMapping.invoiceCompanyOrderReferenceLabel}
              placeHolder={textsMapping.invoiceCompanyOrderReferencePlaceholder}
              path="customerData.companyInvoiceDetails.companyOrderReference"
              required={selectedOption.requiresOrderReference}
            />
          </section>
          <section className="checkout-section forward-email-invoice">
            <h3 className="checkout-section__title">
              {textsMapping.invoiceCompanyEmailTitle}
            </h3>

            <p>{textsMapping.invoiceCompanyEmailText}</p>

            <Input
              resourcedTexts={textsMapping}
              inputType="email"
              inputLabel={textsMapping.invoiceCompanyEmailLabel}
              placeHolder={textsMapping.invoiceCompanyEmailPlaceholder}
              path="customerData.companyInvoiceDetails.companyInvoiceEmail"
              errorMessage={textsMapping.inputErrorMessageEmail}
              required
            />
          </section>
        </>
      )}
    </>
  );
}
